import React, { ChangeEvent } from 'react'
import clsx from 'clsx'
import { TimePicker as TimePickerStyles } from '@airbus/elements/elements'
import { createUseStyles } from '@airbus/components-react/lib/styling'
import useDeprecatedProps from '@airbus/components-react/lib/hooks/useDeprecatedProps'
import useControlled from '@airbus/components-react/lib/hooks/useControlled'
import useUpdateEffect from '@airbus/components-react/lib/hooks/useUpdateEffect'
import { formatWithSeparators, getRegexpIndices } from '@airbus/components-react/lib/utils'
import ComboBox, { ComboBoxDefaultOption } from '@airbus/components-react/components/ComboBox'
import Input, { InputProps } from '@airbus/components-react/components/Input'
import IconButton from '@airbus/components-react/components/IconButton'
import { Clear } from '@mui/icons-material'
import { CommonProps } from '@airbus/components-react/components/helperTypes'

// TODO add showSecond prop ?

type Props = {
  /** Duration pattern */
  pattern: string
  /** The default value to be used for an uncontrolled component */
  defaultValue?: string
  /** customise props passed to the Input component */
  inputProps?: InputProps
  /** optional react ref to pass to native input */
  inputRef?: React.RefObject<HTMLInputElement>
  /** The callback for when the input's value changes */
  onChange?: (value: string) => void
  /** Function called when time format validation status is updated */
  onValidation?: (valid: boolean) => void
  /** Show the button clear in input box */
  showClearButton?: boolean
  /** Format time of 12 hours */
  use12Hours?: boolean
  /** text value */
  value?: string
}

type DeprecatedProps = {
  isOpen?: boolean
  title?: string
}

export type TimePickerProps = Props & DeprecatedProps & CommonProps

const useStyles = createUseStyles(TimePickerStyles, 'timepicker')

const PERIODS = [
  { label: 'AM', value: 'am' },
  { label: 'PM', value: 'pm' },
]
const FORMAT: string = 'hh:mm'
const SEPARATOR: string = ':'
const SEPARATOR_REGEXP = new RegExp(':', 'g')
const SEPARATOR_INDICES: number[] = getRegexpIndices(FORMAT, SEPARATOR_REGEXP)

interface TimePickerState {
  /** Duration pattern */
  pattern: string
  /** valid time value combined from input and period */
  match?: string
  /** input value */
  input: string
  /** invalid state */
  invalid: boolean
  /** period value */
  period: string
  /** flag to use 12 or 24 hour cycles */
  use12Hours: boolean
}

// state mutations types
type TimePickerAction =
  | {
      type: 'updateInput'
      input: string
      selectionStart: number | null
      addSeparators?: boolean
    }
  | { type: 'updatePeriod'; period: string }
  | { type: 'checkInput' }

// state mutations reducer
export const durationPickerReducer = (prevState: TimePickerState, action: TimePickerAction): TimePickerState => {
  const pattern: string = prevState.pattern
  const getTimeMatch = (input: string, period: string, pattern: string, use12Hours: boolean) => {
    let result = input
    const match: boolean = input.match(pattern) !== null
    if (match || input === '') {
      if (use12Hours && input !== '') {
        // add space only if period is present.
        // This is essential as we split the input based on space
        result = `${input}${period ? ' ' + period : ''}`
      }
      return result
    }
    return undefined
  }

  switch (action.type) {
    case 'updateInput':
      // input can come with/without period
      let newValue = action.input
      const selectionStart = action.selectionStart
      // cursor moved inside the date string > restart from here
      if (selectionStart && newValue.length > selectionStart) {
        newValue = newValue.slice(0, selectionStart)
      }
      // automatically add separators by following time format
      if (action.addSeparators) {
        newValue = formatWithSeparators(newValue, FORMAT, SEPARATOR, SEPARATOR_REGEXP, SEPARATOR_INDICES)
      }
      // splitting based on space to get period if present
      const split = newValue.split(' ')
      // update state.match value
      // - new input value is an emtpy string (has been cleared by user)
      // - we have a time format matching input
      const match =
        newValue === '' ? newValue : getTimeMatch(split[0], split[1] || prevState.period, pattern, prevState.use12Hours)
      return {
        ...prevState,
        input: split[0],
        match,
        // reset invalid state if we have a match or empty string
        invalid: match !== undefined ? false : prevState.invalid,
        period: split[1] || prevState.period,
      }
    case 'updatePeriod':
      return {
        ...prevState,
        period: action.period,
        match: getTimeMatch(prevState.input, action.period, pattern, prevState.use12Hours),
      }
    case 'checkInput':
      return {
        ...prevState,
        invalid: prevState.input !== '' && prevState.input.match(pattern) === null,
      }
    default:
      throw new Error()
  }
}

const DurationPicker = (props: TimePickerProps) => {
  const {
    className,
    defaultValue,
    inputProps,
    inputRef,
    onChange,
    onValidation,
    showClearButton,
    value: valueProp,
    use12Hours = false,
    pattern,
    ...rest
  } = props
  useDeprecatedProps('TimePicker', { isOpen: undefined, title: undefined }, props)
  const classes = useStyles()

  // controlled/uncontrolled value
  const [value, setValue] = useControlled({
    value: valueProp,
    defaultValue,
  })

  // state management
  const initialState: TimePickerState = React.useMemo(() => {
    if (!value) {
      return {
        pattern: '[0-9][0-9]:[0-5][0-9]',
        input: '',
        invalid: false,
        period: 'am',
        use12Hours,
      }
    }
    const split = value.split(' ')
    return {
      pattern,
      input: use12Hours ? split[0] : value,
      invalid: false,
      period: use12Hours ? split[1] : '',
      use12Hours,
    }
  }, [use12Hours, value])
  const [state, dispatch] = React.useReducer(durationPickerReducer, initialState)

  // update reducer when controlled value changes
  useUpdateEffect(() => {
    if (!valueProp || valueProp === state.input) return
    dispatch({
      type: 'updateInput',
      input: valueProp,
      selectionStart: 0,
      addSeparators: false,
    })
  }, [valueProp])

  // update matching value and dispatch change
  useUpdateEffect(() => {
    if (state.match === valueProp) return
    if (state.match !== undefined) {
      onChange && onChange(state.match)
    } else {
      onChange && onChange(state.input)
    }
    setValue(state.input)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, state.input, state.match])

  // dispatch validation updates
  useUpdateEffect(() => {
    onValidation && onValidation(!state.invalid)
  }, [state.invalid])

  const handleChange = React.useCallback((evt: ChangeEvent<HTMLInputElement>) => {
    let { value: newValue, selectionStart } = evt.target
    const { inputType } = evt.nativeEvent as InputEvent
    dispatch({
      type: 'updateInput',
      input: newValue,
      selectionStart,
      addSeparators: (inputType && inputType.indexOf('inser') >= 0) as boolean,
    })
  }, [])

  const clear = () => {
    dispatch({
      type: 'updateInput',
      input: '',
      selectionStart: 0,
      addSeparators: false,
    })
  }
  return (
    <div className={clsx(className, classes.root)} {...rest}>
      <Input
        endAdornment={
          showClearButton &&
          state.input !== '' && (
            <IconButton variant='ghost' size='xsmall' aria-label='Clear date' onClick={() => clear()}>
              <Clear />
            </IconButton>
          )
        }
        onBlur={() => dispatch({ type: 'checkInput' })}
        onChange={handleChange}
        invalid={state.invalid}
        pattern={pattern}
        placeholder={FORMAT}
        inputRef={inputRef}
        value={state.input}
        {...inputProps}
      />
      {use12Hours && (
        <ComboBox
          aria-label='Choose period'
          className={classes.period}
          hideClear
          options={PERIODS}
          value={state.period || ''}
          onChange={(option: ComboBoxDefaultOption) => dispatch({ type: 'updatePeriod', period: option.value })}
        />
      )}
    </div>
  )
}

export default DurationPicker
